import { difference, equals } from 'ramda'
import {
  type FC,
  type ReactNode,
  type Reducer,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { hasPermissions } from 'app/plugins/openstack/helpers'
import type { Align } from 'core/elements/tooltip/Tooltip'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { emptyArr, isNilOrEmpty, noop } from 'utils/fp'
import GridDefaultActionButton from '../buttons/GridDefaultActionButton'
import GridDefaultDropdownActionButton from '../buttons/GridDefaultDropdownActionButton'
import type { ParsedGridRow } from './useGridRows'

export interface GridBatchActionsConfig<T> {
  multiSelection?: boolean
  disableRowSelection?: boolean
  rowIsSelectableFn?: (item: T) => boolean
  selectedItems?: T[]
  onSelectChange?: (selectedItems: T[]) => void
  isControlled?: boolean
  totalItems?: number
  batchActions?: Array<GridBatchActionSpec<T>>
  dropdownBatchActions?: Array<GridDropdownBatchActionGroup<T>>
  onRefresh?: (...args: unknown[]) => void | Promise<void>
  isBackgroundRefresh?: boolean
}

export interface BatchActionButtonProps<T> {
  onClick?: () => void | Promise<void>
  children: string | ReactNode
  disabled?: boolean
  icon?: string
  iconOnly?: boolean
  className?: string
  closeDropdown?: () => void
}

export interface GridBatchActionSpec<T> {
  cond?: (selectedItems: T[]) => boolean
  disabledTooltip?: string | ((selectedItems: T[]) => string | Promise<string> | ReactNode)
  tooltip?: string
  align?: Align
  label?: string | ReactNode
  icon?: string
  iconOnly?: boolean
  handleAction?: (selectedItems: T[]) => boolean | void | Promise<boolean | void>
  keepRowsSelected?: boolean
  refreshAfterSuccess?: boolean
  onComplete?: (success, selectedItems: T[]) => boolean | void | Promise<boolean | void>
  BatchActionButton?: FC<BatchActionButtonProps<T>>
  permissionsPath?: string[]
}

export interface GridDropdownBatchActionGroup<T> {
  label: string
  icon?: string
  actions: GridDropdownBatchActionSpec<T>[]
}

export interface GridDropdownBatchActionSpec<T> {
  cond?: (selectedItems: T[]) => boolean
  hideCond?: (selectedItems: T[]) => boolean
  label?: string | ReactNode
  icon?: string
  tooltip?: string
  disabledTooltip?: string | ((selectedItems: T[]) => string | Promise<string> | ReactNode)
  align?: Align
  handleAction?: (selectedItems: T[]) => boolean | void | Promise<boolean | void>
  keepRowsSelected?: boolean
  refreshAfterSuccess?: boolean
  onComplete?: (success, selectedItems: T[]) => boolean | void | Promise<boolean | void>
  BatchActionButton?: FC<BatchActionButtonProps<T>>
  permissionsPath?: string[]
}

export interface SelectableParsedGridRow<T> extends ParsedGridRow<T> {
  isSelected?: boolean
  isSelectable?: boolean
  multiSelection?: boolean
  toggleSelect?: () => void
  select?: () => void
  unselect?: () => void
}

export type SelectionStatus = 'all' | 'some' | 'none'

export interface GridBatchActionGroupProps<T> {
  label: string
  icon: string
  actions: GridBatchActionProps<T>[]
  renderOutsideGrid?: boolean
  styledButton?: boolean
}

export interface GridBatchActionProps<T> {
  key: string | number
  label: string | ReactNode
  icon?: string
  iconOnly?: boolean
  disabled: boolean
  hideIfDisabled?: boolean
  tooltip?: string | ReactNode
  disabledTooltip?: string | ((selectedItems: T[]) => string | Promise<string> | ReactNode)
  align?: Align
  triggerAction?: () => void
  BatchActionButton: FC<BatchActionButtonProps<T>>
}

export interface GridBatchActionsProps<T> {
  batchActionsDisabled: boolean
  batchActions?: GridBatchActionProps<T>[]
  // dropdownBatchActions?: GridBatchActionProps<T>[]
  dropdownBatchActions?: GridBatchActionGroupProps<T>[]
  rowsSelectionDisabled: boolean
  multiSelectionEnabled?: boolean
  selectedItems?: T[]
  selectedCount?: number
  toggleSelectAll?: () => void
  clearSelectedRows?: () => void
  selectionStatus?: SelectionStatus
  isBackgroundRefresh?: boolean
  isControlled?: boolean
  pruneToVisibleKeys?: (visibleKeys: Set<string>) => void
}

interface SelectedRowsReducerAction<T> {
  type: 'add' | 'remove' | 'toggle' | 'addSome' | 'removeSome' | 'set' | 'clear'
  payload: {
    row?: ParsedGridRow<T>
    rows?: ParsedGridRow<T>[]
    multiSelection?: boolean
  }
}

type SelectedRowReducer<T> = Reducer<Map<string, ParsedGridRow<T>>, SelectedRowsReducerAction<T>>

const selectedRowReducer = <T>(
  selectedRows: Map<string, ParsedGridRow<T>>,
  { type, payload: { row, rows, multiSelection } }: SelectedRowsReducerAction<T>,
) => {
  const newMap = new Map<string, ParsedGridRow<T>>(
    type === 'set' ? rows.map((row) => [row.key, row]) : selectedRows,
  )
  switch (type) {
    case 'addSome':
      rows.forEach((row) => newMap.set(row.key, row))
      break
    case 'removeSome':
      rows.forEach(({ key }) => newMap.delete(key))
      break
    case 'clear':
      newMap.clear()
      break
    case 'add':
      newMap.set(row.key, row)
      break
    case 'remove':
      newMap.delete(row.key)
      break
    case 'toggle':
      if (newMap.has(row.key)) {
        newMap.delete(row.key)
        break
      }
      if (!multiSelection) {
        newMap.clear()
      }
      newMap.set(row.key, row)
      break
  }
  return newMap
}

const maxSize = 1000000

export default function useGridSelectableRows<T>(
  rows: Array<ParsedGridRow<T>>,
  {
    batchActions: rowActionsSpec = emptyArr,
    dropdownBatchActions: rowDropdownActionsSpec = emptyArr,
    multiSelection,
    rowIsSelectableFn,
    totalItems = rows.length,
    selectedItems,
    onSelectChange,
    isControlled = !!selectedItems,
    disableRowSelection = isNilOrEmpty(rowActionsSpec) &&
      isNilOrEmpty(rowDropdownActionsSpec) &&
      !isControlled,
    onRefresh,
    isBackgroundRefresh,
  }: GridBatchActionsConfig<T>,
): [Array<SelectableParsedGridRow<T>>, GridBatchActionsProps<T>] {
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs

  const instanceId = useId()
  const gridId = useRef(instanceId)

  const initialState = useMemo(() => new Map<string, ParsedGridRow<T>>(), [])
  // @ts-ignore
  const [selectedRows, dispatch] = useReducer<SelectedRowReducer<T>>(
    selectedRowReducer,
    initialState,
  )
  const [locallySelectedItems, setLocallySelectedItems] = useState([])

  useEffect(() => {
    // @ts-ignore
    setLocallySelectedItems((prev) => {
      // @ts-ignore
      const next = Array.from(selectedRows.values()).map(({ item }) => item)
      // Bail out (no re-render) when nothing structurally changed.
      // This prevents extra re-renders on every poll cycle when no rows
      // are selected, while still re-syncing when fresh data arrives for
      // selected items (e.g. updated volumeDetails after an ISO mount).
      return equals(prev, next) ? prev : next
    })
  }, [selectedRows, selectedItems, rows])

  useEffect(() => {
    // current tenant changed, clear selections
    clearSelectedRows()
  }, [currentTenant])

  useEffect(() => {
    if (selectedRows.size === 0) return
    window.dispatchEvent(
      new CustomEvent('pcd-grid-selection-active', { detail: { gridId: gridId.current } }),
    )
  }, [selectedRows.size])

  useEffect(() => {
    const handler = (e: CustomEvent<{ gridId: string }>) => {
      if (e.detail.gridId !== gridId.current) {
        dispatch({ type: 'clear', payload: {} })
      }
    }
    window.addEventListener('pcd-grid-selection-active', handler as EventListener)
    return () => window.removeEventListener('pcd-grid-selection-active', handler as EventListener)
  }, [])

  useEffect(() => {
    // This should only happen when locally selected
    // items are out of sync (ie changed by an external agent)
    if (isControlled && !equals(selectedItems, locallySelectedItems)) {
      const selectedRows = rows.filter((row) => selectedItems.includes(row.item))
      dispatch({ type: 'set', payload: { rows: selectedRows } })
    }
  }, [selectedItems, rows])

  useEffect(() => {
    if (!isControlled || equals(selectedItems, locallySelectedItems)) return
    onSelectChange && onSelectChange(locallySelectedItems)
  }, [locallySelectedItems, onSelectChange])

  // Using memoize here produces massive lag for large lists, better to just not
  // const getSelectToggler = useCallback(
  //   memoize(
  //     (row, type: 'toggle' | 'add' | 'remove') => () => {
  //       dispatch({ type, payload: { row, multiSelection } })
  //     },
  //     { maxSize },
  //   ),
  //   [multiSelection],
  // )

  const getSelectToggler = useCallback(
    (row, type: 'toggle' | 'add' | 'remove') => () => {
      dispatch({ type, payload: { row, multiSelection } })
    },
    [multiSelection],
  )

  const getRowIsSelectable = useCallback(
    (row, isSelected) => {
      const isSelectable = rowIsSelectableFn ? rowIsSelectableFn(row.item) : true

      return {
        ...row,
        isSelectable,
        isSelected,
        multiSelection,
        toggleSelect: isSelectable ? getSelectToggler(row, 'toggle') : noop,
        select: isSelectable ? getSelectToggler(row, 'add') : noop,
        unselect: isSelectable ? getSelectToggler(row, 'remove') : noop,
      }
    },
    [multiSelection, rowIsSelectableFn],
  )

  // This must be changed, the .map on the large array takes too long, and starts
  // to get backed up over time. Perhaps on lists above X number of entries, only update this
  // every so often
  const selectableRows = useMemo<Array<SelectableParsedGridRow<T>>>(
    () =>
      rows.map((row) => {
        const { key } = row
        // @ts-ignore
        const isSelected = selectedRows.has(key)
        if (isSelected) {
          // Update the existing row
          // @ts-ignore
          selectedRows.set(key, row)
        }
        return rowIsSelectableFn
          ? getRowIsSelectable(row, isSelected)
          : {
              ...row,
              isSelectable: true,
              isSelected,
              multiSelection,
              toggleSelect: getSelectToggler(row, 'toggle'),
              select: getSelectToggler(row, 'add'),
              unselect: getSelectToggler(row, 'remove'),
            }
      }),
    [rows, selectedRows, getRowIsSelectable, rowIsSelectableFn],
  )

  const toggleSelectAll = useCallback(
    (rows = selectableRows) => {
      // Filter only selectable rows once to avoid redundant iterations
      const selectable = rows.filter((r) => r.isSelectable)
      if (!selectable.length) return // No selectable rows → nothing to do

      // Create a Set for faster O(1) key lookups
      const keys = selectable.map((r) => r.key)
      const selectedKeys = selectedRows ? new Set(selectedRows.keys()) : new Set()

      // "All selected" means every row on THIS page is already in the selection.
      // Test membership, not counts. Comparing this page's count to the total
      // selected count (which spans other pages under server-side pagination)
      // false-positives on page 2+ and makes Select All a no-op. SELECTROWS-FR-016.
      const allSelected = keys.every((key) => selectedKeys.has(key))

      // If all are selected → remove them, otherwise → add them
      dispatch({
        type: allSelected ? 'removeSome' : 'addSome',
        payload: { rows: allSelected ? rows : selectable },
      })
    },
    [selectableRows, selectedRows],
  )
  const clearSelectedRows = useCallback(() => {
    dispatch({ type: 'clear', payload: {} })
  }, [])

  // Drops selected keys no longer present in the currently visible (post-filter) row set,
  // so a search/filter that hides a selected row doesn't leave it stuck selected forever.
  const pruneToVisibleKeys = useCallback(
    (visibleKeys: Set<string>) => {
      const staleKeys = [...selectedRows.keys()].filter((key) => !visibleKeys.has(key))
      if (!staleKeys.length) return
      dispatch({
        type: 'removeSome',
        payload: { rows: staleKeys.map((key) => ({ key }) as ParsedGridRow<T>) },
      })
    },
    [selectedRows],
  )

  const getTooltipContent = (items, cond, disabledTooltip, tooltip) => {
    if (cond && !cond(items)) {
      if (typeof disabledTooltip === 'function') {
        return disabledTooltip(items)
      }
      return disabledTooltip || tooltip
    }
    return tooltip
  }

  const batchActions = useMemo<GridBatchActionProps<T>[]>(() => {
    if (!rowActionsSpec) {
      return emptyArr
    }
    return rowActionsSpec.map(
      (
        {
          cond,
          disabledTooltip,
          tooltip,
          align,
          BatchActionButton = GridDefaultActionButton,
          label,
          keepRowsSelected,
          refreshAfterSuccess = true,
          onComplete,
          handleAction,
          icon,
          iconOnly,
          permissionsPath,
        },
        idx,
      ) => ({
        key: idx,
        BatchActionButton: BatchActionButton,
        label,
        disabled:
          !locallySelectedItems.length ||
          (permissionsPath && !hasPermissions(permissionsPath)) ||
          (cond && !cond(locallySelectedItems)),
        tooltip:
          permissionsPath && !hasPermissions(permissionsPath)
            ? 'You do not have permissions for that action'
            : getTooltipContent(locallySelectedItems, cond, disabledTooltip, tooltip),
        align: align,
        triggerAction: async () => {
          const success = handleAction ? await handleAction(locallySelectedItems) : true
          if (success !== false) {
            if (refreshAfterSuccess && onRefresh) {
              onRefresh()
            }
            if (!keepRowsSelected) {
              dispatch({ type: 'clear', payload: {} })
            }
          }
          if (onComplete) {
            onComplete(success, locallySelectedItems)
          }
        },
        icon,
        iconOnly,
      }),
    )
  }, [rowActionsSpec, locallySelectedItems])

  const dropdownBatchActions = useMemo<GridBatchActionGroupProps<T>[]>(() => {
    if (!rowDropdownActionsSpec) {
      return emptyArr
    }
    return rowDropdownActionsSpec.map(({ icon, label, actions }) => ({
      icon: icon,
      label: label,
      actions: actions
        .filter(({ hideCond }) => {
          return hideCond ? !hideCond(locallySelectedItems) : true
        })
        .map(
          (
            {
              cond,
              BatchActionButton = GridDefaultDropdownActionButton,
              label,
              keepRowsSelected = true,
              refreshAfterSuccess = true,
              onComplete,
              handleAction,
              icon,
              tooltip,
              disabledTooltip,
              align,
              permissionsPath,
            },
            idx,
          ) => ({
            key: idx,
            BatchActionButton: BatchActionButton,
            label,
            icon,
            align,
            disabled:
              !locallySelectedItems.length ||
              (permissionsPath && !hasPermissions(permissionsPath)) ||
              (cond && !cond(locallySelectedItems)),
            tooltip:
              permissionsPath && !hasPermissions(permissionsPath)
                ? 'You do not have permissions for that action'
                : cond && !cond(locallySelectedItems)
                  ? typeof disabledTooltip === 'function'
                    ? disabledTooltip(locallySelectedItems)
                    : disabledTooltip || tooltip
                  : tooltip,
            // This doesn't actually get used in GridDialogButton
            triggerAction: async () => {
              const success = handleAction ? await handleAction(locallySelectedItems) : true
              if (success !== false) {
                if (refreshAfterSuccess && onRefresh) {
                  onRefresh()
                }
                if (onComplete) {
                  onComplete(success, locallySelectedItems)
                }
                if (!keepRowsSelected) {
                  dispatch({ type: 'clear', payload: {} })
                }
              }
            },
          }),
        ),
    }))
  }, [rowDropdownActionsSpec, locallySelectedItems])

  // @ts-ignore
  const selectedCount = selectedRows.size
  const selectionStatus = selectedCount === totalItems ? 'all' : selectedCount ? 'some' : 'none'

  if (disableRowSelection) {
    return [rows, { rowsSelectionDisabled: true, batchActionsDisabled: true }]
  }

  return [
    selectableRows,
    {
      toggleSelectAll,
      clearSelectedRows,
      pruneToVisibleKeys,
      isControlled,
      selectedItems: locallySelectedItems,
      selectedCount,
      selectionStatus,
      multiSelectionEnabled: multiSelection,
      rowsSelectionDisabled: false,
      batchActionsDisabled: isNilOrEmpty(rowActionsSpec),
      batchActions,
      dropdownBatchActions,
      isBackgroundRefresh,
    },
  ]
}
